import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, Flame, Star, Lock, CheckCircle, Loader2, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";

const Achievements = () => {
  const { token } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [achievementsRes, streakRes] = await Promise.all([
          axios.get(`${API}/meditations/achievements`, { headers, withCredentials: true }),
          axios.get(`${API}/meditations/streak`, { headers, withCredentials: true })
        ]);
        
        setAchievements(achievementsRes.data.achievements || []);
        setStreakData(streakRes.data);
      } catch (error) {
        // data loads with fallback state
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!token) {
    return (
      <div className="space-y-6" data-testid="achievements-page">
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <Trophy className="w-16 h-16 text-mystic-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Sign In Required</h2>
            <p className="text-mystic-200/70 mb-4">Please sign in to view your meditation achievements.</p>
            <Link to="/login">
              <Button className="bg-mystic-600 hover:bg-mystic-500 text-white">
                Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  const earnedCount = achievements.filter(a => a.earned).length;
  const totalCount = achievements.length;

  return (
    <div className="space-y-6" data-testid="achievements-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Link to="/dashboard" className="inline-flex items-center text-mystic-300 hover:text-white mb-4 transition-colors">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Trophy className="w-8 h-8 text-amber-400" />
          Meditation Achievements
        </h1>
        <p className="text-mystic-200/70">Track your meditation journey and unlock badges</p>
      </motion.div>

      {/* Stats Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="glass bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
          <CardContent className="py-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Trophy className="w-6 h-6 text-amber-400" />
                  <span className="text-3xl font-bold text-white">{earnedCount}</span>
                </div>
                <p className="text-sm text-mystic-300">Badges Earned</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Flame className={`w-6 h-6 ${streakData?.current_streak > 0 ? 'text-orange-400' : 'text-mystic-500'}`} />
                  <span className="text-3xl font-bold text-white">{streakData?.current_streak || 0}</span>
                </div>
                <p className="text-sm text-mystic-300">Current Streak</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{streakData?.longest_streak || 0}</div>
                <p className="text-sm text-mystic-300">Longest Streak</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{streakData?.total_sessions || 0}</div>
                <p className="text-sm text-mystic-300">Total Sessions</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{streakData?.total_minutes || 0}</div>
                <p className="text-sm text-mystic-300">Minutes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Progress Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        <Card className="glass">
          <CardContent className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-medium">Overall Progress</span>
              <span className="text-mystic-300">{earnedCount}/{totalCount} badges</span>
            </div>
            <div className="w-full h-3 bg-mystic-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(earnedCount / totalCount) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {achievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + index * 0.05 }}
          >
            <Card className={`glass h-full transition-all ${
              achievement.earned 
                ? 'border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-orange-500/5' 
                : 'border-mystic-700/30 opacity-70'
            }`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${
                    achievement.earned 
                      ? 'bg-amber-500/20' 
                      : 'bg-mystic-800/50'
                  }`}>
                    {achievement.earned ? achievement.icon : <Lock className="w-6 h-6 text-mystic-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-semibold ${achievement.earned ? 'text-white' : 'text-mystic-400'}`}>
                        {achievement.name}
                      </h3>
                      {achievement.earned && (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      )}
                    </div>
                    <p className={`text-sm mb-2 ${achievement.earned ? 'text-mystic-200/80' : 'text-mystic-400/60'}`}>
                      {achievement.description}
                    </p>
                    {!achievement.earned && achievement.progress_text && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-mystic-400 mb-1">
                          <span>Progress</span>
                          <span>{achievement.progress_text}</span>
                        </div>
                        <div className="w-full h-1.5 bg-mystic-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-mystic-500 rounded-full transition-all"
                            style={{ width: `${achievement.progress * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {achievement.earned && achievement.earned_at && (
                      <p className="text-xs text-mystic-400 mt-2">
                        Earned {new Date(achievement.earned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Motivational Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="glass-heavy">
          <CardContent className="py-8 text-center">
            <Star className="w-12 h-12 text-mystic-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              {earnedCount === 0 
                ? "Begin Your Sacred Journey" 
                : earnedCount < 5 
                  ? "You're Making Progress!" 
                  : earnedCount < 10 
                    ? "Wonderful Dedication!" 
                    : "You're a True Moon Child!"}
            </h3>
            <p className="text-mystic-200/70 max-w-md mx-auto">
              {earnedCount === 0 
                ? "Start meditating today to earn your first badge and begin building your streak." 
                : `You've earned ${earnedCount} of ${totalCount} badges. Keep meditating daily to unlock more achievements!`}
            </p>
            <Link to="/rituals">
              <Button className="mt-4 bg-mystic-600 hover:bg-mystic-500 text-white">
                Start Meditating
              </Button>
            </Link>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default Achievements;
